// TheCodeMinigame.jsx - 8-bit bug-squashing minigame for "The Code" aircraft
import React, { useEffect, useRef, useState, useCallback } from 'react';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 400;
const PLANE_SIZE = 48;
const BUG_SIZE = 32;
const POWERUP_SIZE = 24;
const GAME_DURATION = 30; // seconds

// Pixel art sprites as data URLs
const SPRITES = {
  // Bug sprite - 8-bit ladybug style
  bug: `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
      <rect x="12" y="4" width="8" height="4" fill="#222"/>
      <rect x="8" y="8" width="16" height="4" fill="#e53935"/>
      <rect x="4" y="12" width="24" height="12" fill="#e53935"/>
      <rect x="8" y="24" width="16" height="4" fill="#e53935"/>
      <rect x="14" y="8" width="4" height="20" fill="#222"/>
      <rect x="8" y="14" width="4" height="4" fill="#222"/>
      <rect x="20" y="14" width="4" height="4" fill="#222"/>
      <rect x="10" y="20" width="4" height="4" fill="#222"/>
      <rect x="18" y="20" width="4" height="4" fill="#222"/>
      <rect x="4" y="8" width="4" height="4" fill="#222"/>
      <rect x="24" y="8" width="4" height="4" fill="#222"/>
    </svg>
  `)}`,

  // Coffee cup
  coffee: `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <rect x="4" y="8" width="12" height="14" fill="#8B4513"/>
      <rect x="6" y="10" width="8" height="4" fill="#D2691E"/>
      <rect x="16" y="10" width="4" height="8" fill="none" stroke="#8B4513" stroke-width="2"/>
      <rect x="6" y="4" width="2" height="4" fill="#888"/>
      <rect x="10" y="2" width="2" height="6" fill="#888"/>
      <rect x="14" y="4" width="2" height="4" fill="#888"/>
    </svg>
  `)}`,

  // Semicolon
  semicolon: `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <circle cx="12" cy="8" r="4" fill="#f59e0b"/>
      <circle cx="12" cy="18" r="4" fill="#f59e0b"/>
      <rect x="10" y="18" width="4" height="6" fill="#f59e0b"/>
    </svg>
  `)}`,

  // Curly brace
  brace: `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <text x="4" y="20" font-family="monospace" font-size="24" font-weight="bold" fill="#60ffd0">{}</text>
    </svg>
  `)}`,
};

// Konami code sequence
const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

export default function TheCodeMinigame({ onComplete, onFail, planeImageSrc }) {
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const [gameState, setGameState] = useState('starting'); // starting, playing, won, lost
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [konamiIndex, setKonamiIndex] = useState(0);
  const [konamiActivated, setKonamiActivated] = useState(false);
  const [message, setMessage] = useState('');

  // Game objects refs (to avoid re-renders)
  const planeRef = useRef({ x: 100, y: GAME_HEIGHT / 2, vy: 0 });
  const bugsRef = useRef([]);
  const powerupsRef = useRef([]);
  const keysRef = useRef({ up: false, down: false });
  const livesRef = useRef(3);
  const scoreRef = useRef(0);
  const imagesRef = useRef({});

  // Load images
  useEffect(() => {
    const loadImage = (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      });
    };

    Promise.all([
      loadImage(planeImageSrc || './aircraft/collectibles/anomalous/the-code.png'),
      loadImage(SPRITES.bug),
      loadImage(SPRITES.coffee),
      loadImage(SPRITES.semicolon),
      loadImage(SPRITES.brace),
    ]).then(([plane, bug, coffee, semicolon, brace]) => {
      imagesRef.current = { plane, bug, coffee, semicolon, brace };
      // Start game after brief delay
      setTimeout(() => setGameState('playing'), 1500);
    });
  }, [planeImageSrc]);

  // Konami code listener
  useEffect(() => {
    const handleKonami = (e) => {
      const key = e.key;
      if (key === KONAMI_CODE[konamiIndex]) {
        const newIndex = konamiIndex + 1;
        setKonamiIndex(newIndex);
        if (newIndex === KONAMI_CODE.length) {
          setKonamiActivated(true);
          setMessage('GODMODE ACTIVATED');
          livesRef.current = 99;
          setLives(99);
          setTimeout(() => setMessage(''), 2000);
        }
      } else if (key === KONAMI_CODE[0]) {
        setKonamiIndex(1);
      } else {
        setKonamiIndex(0);
      }
    };

    window.addEventListener('keydown', handleKonami);
    return () => window.removeEventListener('keydown', handleKonami);
  }, [konamiIndex]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp' || e.key === 'w') keysRef.current.up = true;
      if (e.key === 'ArrowDown' || e.key === 's') keysRef.current.down = true;
    };
    const handleKeyUp = (e) => {
      if (e.key === 'ArrowUp' || e.key === 'w') keysRef.current.up = false;
      if (e.key === 'ArrowDown' || e.key === 's') keysRef.current.down = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Timer
  useEffect(() => {
    if (gameState !== 'playing') return;

    const timer = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          setGameState('won');
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState]);

  // Main game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let frameCount = 0;

    const spawnBug = () => {
      bugsRef.current.push({
        x: GAME_WIDTH + BUG_SIZE,
        y: Math.random() * (GAME_HEIGHT - BUG_SIZE * 2) + BUG_SIZE,
        speed: 3 + Math.random() * 3,
        wobble: Math.random() * Math.PI * 2,
      });
    };

    const spawnPowerup = () => {
      const types = ['coffee', 'semicolon', 'brace'];
      powerupsRef.current.push({
        x: GAME_WIDTH + POWERUP_SIZE,
        y: Math.random() * (GAME_HEIGHT - POWERUP_SIZE * 2) + POWERUP_SIZE,
        speed: 2 + Math.random() * 2,
        type: types[Math.floor(Math.random() * types.length)],
      });
    };

    const checkCollision = (a, aSize, b, bSize) => {
      return a.x < b.x + bSize &&
             a.x + aSize > b.x &&
             a.y < b.y + bSize &&
             a.y + aSize > b.y;
    };

    const gameLoop = () => {
      frameCount++;
      const plane = planeRef.current;
      const keys = keysRef.current;

      // Clear with dark background
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

      // Draw scanlines for retro effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      for (let i = 0; i < GAME_HEIGHT; i += 4) {
        ctx.fillRect(0, i, GAME_WIDTH, 2);
      }

      // Draw scrolling stars/bits
      ctx.fillStyle = '#333';
      for (let i = 0; i < 50; i++) {
        const x = (frameCount * (1 + i % 3) + i * 37) % GAME_WIDTH;
        const y = (i * 73) % GAME_HEIGHT;
        ctx.fillRect(x, y, 2, 2);
      }

      // Update plane
      if (keys.up) plane.vy -= 0.8;
      if (keys.down) plane.vy += 0.8;
      plane.vy *= 0.92; // friction
      plane.y += plane.vy;
      plane.y = Math.max(PLANE_SIZE/2, Math.min(GAME_HEIGHT - PLANE_SIZE/2, plane.y));

      // Draw plane
      if (imagesRef.current.plane) {
        ctx.save();
        ctx.translate(plane.x, plane.y);
        ctx.scale(-1, 1); // Flip to face right
        // Slight tilt based on velocity
        ctx.rotate(plane.vy * 0.02);
        ctx.drawImage(imagesRef.current.plane, -PLANE_SIZE/2, -PLANE_SIZE/2, PLANE_SIZE, PLANE_SIZE);
        ctx.restore();
      }

      // Spawn enemies
      if (frameCount % 45 === 0) spawnBug();
      if (frameCount % 120 === 0) spawnPowerup();

      // Update and draw bugs
      bugsRef.current = bugsRef.current.filter(bug => {
        bug.x -= bug.speed;
        bug.wobble += 0.1;
        const wobbleY = Math.sin(bug.wobble) * 2;

        // Check collision with plane
        if (checkCollision(
          { x: plane.x - PLANE_SIZE/2, y: plane.y - PLANE_SIZE/2 }, PLANE_SIZE * 0.7,
          { x: bug.x - BUG_SIZE/2, y: bug.y + wobbleY - BUG_SIZE/2 }, BUG_SIZE * 0.7
        )) {
          livesRef.current--;
          setLives(livesRef.current);
          setMessage('SEGFAULT!');
          setTimeout(() => setMessage(''), 500);

          if (livesRef.current <= 0) {
            setGameState('lost');
          }
          return false;
        }

        if (bug.x < -BUG_SIZE) return false;

        // Draw bug
        if (imagesRef.current.bug) {
          ctx.drawImage(imagesRef.current.bug, bug.x - BUG_SIZE/2, bug.y + wobbleY - BUG_SIZE/2, BUG_SIZE, BUG_SIZE);
        }
        return true;
      });

      // Update and draw powerups
      powerupsRef.current = powerupsRef.current.filter(pu => {
        pu.x -= pu.speed;

        // Check collision with plane
        if (checkCollision(
          { x: plane.x - PLANE_SIZE/2, y: plane.y - PLANE_SIZE/2 }, PLANE_SIZE * 0.6,
          { x: pu.x - POWERUP_SIZE/2, y: pu.y - POWERUP_SIZE/2 }, POWERUP_SIZE
        )) {
          const points = pu.type === 'coffee' ? 100 : pu.type === 'semicolon' ? 50 : 25;
          scoreRef.current += points;
          setScore(scoreRef.current);
          setMessage(`+${points}`);
          setTimeout(() => setMessage(''), 300);
          return false;
        }

        if (pu.x < -POWERUP_SIZE) return false;

        // Draw powerup
        const img = imagesRef.current[pu.type];
        if (img) {
          ctx.drawImage(img, pu.x - POWERUP_SIZE/2, pu.y - POWERUP_SIZE/2, POWERUP_SIZE, POWERUP_SIZE);
        }
        return true;
      });

      // Draw HUD
      ctx.fillStyle = '#60ffd0';
      ctx.font = 'bold 16px monospace';
      ctx.fillText(`TIME: ${timeLeft}s`, 10, 25);
      ctx.fillText(`SCORE: ${scoreRef.current}`, 10, 45);
      ctx.fillStyle = livesRef.current <= 1 ? '#ef4444' : '#60ffd0';
      ctx.fillText(`LIVES: ${'<3 '.repeat(livesRef.current)}`, GAME_WIDTH - 150, 25);

      // Draw message
      if (message) {
        ctx.fillStyle = konamiActivated && message === 'GODMODE ACTIVATED' ? '#a855f7' : '#fcd34d';
        ctx.font = 'bold 24px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(message, GAME_WIDTH / 2, GAME_HEIGHT / 2);
        ctx.textAlign = 'left';
      }

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameState, timeLeft, message, konamiActivated]);

  // Handle game end
  const handleContinue = useCallback(() => {
    if (gameState === 'won') {
      onComplete?.(scoreRef.current);
    } else {
      onFail?.();
    }
  }, [gameState, onComplete, onFail]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}>

      {/* Starting screen */}
      {gameState === 'starting' && (
        <div className="text-center animate-pulse">
          <div className="text-4xl font-bold mb-4" style={{ color: '#60ffd0', fontFamily: 'monospace' }}>
            LOADING THE CODE...
          </div>
          <div className="text-lg" style={{ color: '#525252' }}>
            Use Arrow Keys or W/S to fly
          </div>
        </div>
      )}

      {/* Game canvas */}
      {(gameState === 'playing' || gameState === 'won' || gameState === 'lost') && (
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={GAME_WIDTH}
            height={GAME_HEIGHT}
            className="border-4 rounded-lg"
            style={{
              borderColor: gameState === 'lost' ? '#ef4444' : gameState === 'won' ? '#60ffd0' : '#333',
              imageRendering: 'pixelated'
            }}
          />

          {/* Win screen */}
          {gameState === 'won' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-lg">
              <div className="text-center">
                <div className="text-5xl font-bold mb-4" style={{ color: '#60ffd0', fontFamily: 'monospace' }}>
                  BUILD SUCCESSFUL
                </div>
                <div className="text-2xl mb-2" style={{ color: '#fcd34d' }}>
                  Score: {score}
                </div>
                <div className="text-lg mb-6" style={{ color: '#a3a3a3' }}>
                  0 errors, 0 warnings
                </div>
                <button
                  onClick={handleContinue}
                  className="px-8 py-3 text-lg font-bold rounded"
                  style={{ backgroundColor: '#60ffd0', color: '#000' }}
                >
                  DEPLOY TO PRODUCTION
                </button>
              </div>
            </div>
          )}

          {/* Lose screen */}
          {gameState === 'lost' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-lg">
              <div className="text-center">
                <div className="text-5xl font-bold mb-4 animate-pulse" style={{ color: '#ef4444', fontFamily: 'monospace' }}>
                  RUNTIME ERROR
                </div>
                <div className="text-lg mb-2" style={{ color: '#fca5a5' }}>
                  Segmentation fault (core dumped)
                </div>
                <div className="text-sm mb-6" style={{ color: '#525252' }}>
                  Score: {score}
                </div>
                <button
                  onClick={handleContinue}
                  className="px-8 py-3 text-lg font-bold rounded"
                  style={{ backgroundColor: '#ef4444', color: '#000' }}
                >
                  STACK TRACE
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      {gameState === 'playing' && (
        <div className="absolute bottom-8 text-center" style={{ color: '#333' }}>
          <div>Dodge bugs! Collect coffee, semicolons, and braces for points.</div>
          <div className="text-xs mt-1">Survive 30 seconds to complete the flight.</div>
        </div>
      )}
    </div>
  );
}
